import { useCallback, useRef } from 'react';

import type {
  CompanyDiscoveryAiOutput,
  TalentDiscoveryAiFilterSearchResponseV2,
} from '@revelio/data-access';

import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import {
  getValidatedPositionedItemsFromLlmContent,
  PRIMARY_FILTER_BY_AI_ENTITY,
} from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import { summarizeChartPreviewTimeline } from './chart-preview-timeline-summary';
import {
  type CustomDashAssistantEntityType,
  type GlobalAssistantEntityType,
  isCustomDashAssistantEntityType,
  postGlobalAssistantSelectReport,
} from './global-ai-assistant-api';
import type { GlobalAiChartPreviewRuntimeSnapshot } from './global-ai-assistant-chart-preview-runtime';
import {
  useGlobalAiCommandStore,
  waitForCommandComplete,
} from './global-ai-assistant-command-store';
import { useGlobalAIAssistant } from './global-ai-assistant-context';
import { toConversationPayload } from './global-ai-assistant-context-policy';
import { buildBoundedConversationContext } from './global-ai-assistant-conversation';
import { getStandardPageMatchForOptionId } from './global-ai-assistant-standard-page-match';
import type {
  ClientToolHandles,
  NavigateToOptionResult,
} from './global-ai-client-tool-deps';
import { createClientToolDeps } from './global-ai-client-tool-deps';
import { executeClientTool } from './global-ai-client-tools';
import {
  evalRecordTurnEnd,
  evalRecordTurnStart,
} from './global-ai-eval-recorder';
import { generateDashboardHeadless } from './global-ai-generate-headless';
import { reportLoopError } from './global-ai-loop-error-telemetry';
import {
  dashboardStreamEventToProgress,
  routePageGenProgress,
} from './global-ai-page-gen-progress';
import {
  createToolProgressSink,
  type ToolEmit,
} from './global-ai-tool-progress';
import { toolStepTitle } from './global-ai-tool-step-titles';
import { runTurnLoop } from './global-ai-turn-loop';
import type {
  TurnMessage,
  TurnStepRequestBody,
} from './global-ai-turn-step-stream';
import { runTurnStep } from './global-ai-turn-step-stream';
import { fetchHeadlessChartDigest } from './headless-digest/headless-digest-service';
import { hasOfferMarker, type OfferKind } from './offer-marker';
import {
  describeStep,
  emptyTrace,
  endTrace,
  finishStep,
  type ReasoningTrace,
  setStepThinking,
  startStep,
  titleStep,
} from './reasoning-trace';
import type { ChartDataDigest } from './turn-outcome-digest';
import {
  summarizeAppliedChartFilters,
  withChartScope,
} from './turn-outcome-digest';

/**
 * How long to poll the chart-preview runtime snapshot for a freshly rendered
 * chart message before giving up and resolving the model a synthetic empty
 * digest. The CustomDash data providers feed the runtime asynchronously after
 * the chart mounts; 30s covers a cold fetch.
 */
const CHART_SETTLE_TIMEOUT_MS = 30_000;
const CHART_SETTLE_POLL_MS = 50;
/** Upper bound on a single apply/update command landing before the loop moves on. */
const COMMAND_COMPLETE_TIMEOUT_MS = 30_000;

/**
 * Upper bound on the page's `NarrationCollector` publishing its widget digests
 * after an `update_current_page` build command is emitted. The bridge streams
 * the page layout, mounts each widget, then publishes
 * `lastPageCollectionOutcome` once every widget's data reporter has settled —
 * a multi-widget cold page can take ~15-20s.
 *
 * MUST exceed the collector's own hard timeout (30s, see `armPageNarration` in
 * the bridge) so the runner keeps polling until the collector actually
 * publishes, rather than giving up early and handing the model an empty digest
 * set (which would make it narrate the page blind).
 */
const PAGE_COLLECTION_TIMEOUT_MS = 32_000;
const PAGE_COLLECTION_POLL_MS = 50;

/**
 * Canonical Talent / Individual Discovery route path and its `__std__` option
 * id. Used by `applyFilters` when `target === 'talent_discovery'` to navigate
 * there before applying filters to the right surface.
 */
const TALENT_DISCOVERY_PATH = '/talent-discovery';
const TALENT_DISCOVERY_OPTION_ID = '__std__:app:talent-discovery';
/**
 * Canonical Company Discovery (screener) route path and its `__std__` option
 * id. Used by `applyFilters` when `target === 'company_discovery'` to navigate
 * there before applying company-discovery filters to the screener surface,
 * mirroring the talent-discovery path above.
 */
const COMPANY_DISCOVERY_PATH = '/company/screener';
const COMPANY_DISCOVERY_OPTION_ID = '__std__:company:screener';

/**
 * Map of `apply_filters` discovery `target` values to the route path + option
 * id we must navigate to before applying. `current_page` (and any unrecognised
 * value) is absent, so `applyFilters` falls back to the current route.
 */
const DISCOVERY_TARGETS: Readonly<
  Record<string, { path: string; optionId: string }>
> = {
  talent_discovery: {
    path: TALENT_DISCOVERY_PATH,
    optionId: TALENT_DISCOVERY_OPTION_ID,
  },
  company_discovery: {
    path: COMPANY_DISCOVERY_PATH,
    optionId: COMPANY_DISCOVERY_OPTION_ID,
  },
};
/**
 * Inputs the runner needs from the drawer that it cannot reconstruct from
 * context/store on its own. These mirror the drawer's existing closures so the
 * loop reuses the exact same machinery the legacy `runAssistantPipeline` does.
 */
export type GlobalAiTurnRunnerDeps = {
  /** Resolve + emit filters for the given path/prompt, mirroring `queueRouteFilterApply`. */
  resolveAndEmitRouteFilters: (args: {
    requestId: string;
    path: string;
    promptForFilters: string;
    entityType: GlobalAssistantEntityType;
  }) => Promise<TalentDiscoveryAiFilterSearchResponseV2>;
  /**
   * Emit an `applyRouteFilters` command for an ALREADY-resolved tdV2 filter
   * response (no AI re-resolution). The `search_filters` server tool resolved
   * the artifact already; this applies it directly via the bridge's
   * route-filter machinery.
   */
  emitResolvedRouteFilters: (args: {
    requestId: string;
    path: string;
    response:
      | TalentDiscoveryAiFilterSearchResponseV2
      | CompanyDiscoveryAiOutput;
    prompt: string;
    entityType: GlobalAssistantEntityType;
    merge?: boolean;
  }) => void;
  /**
   * Navigate to a `__std__:` candidate id. Resolves with a discriminated
   * result: success carries the landed page display name; failure carries a
   * `reason` (no_access / unknown_page / cancelled) so callers never report a
   * navigation that didn't happen (FE-2117).
   */
  navigateToOptionId: (optionId: string) => Promise<NavigateToOptionResult>;
  /** Emit an `update_current_page` stream command; resolve when it lands. */
  emitUpdateCurrentPage: (requestId: string, prompt: string) => void;
  /**
   * Confirmed-build signal shared with the drawer. The `create_custom_page`
   * tool OFFERS a build-confirm chip by default; when the user clicks that
   * chip the drawer sets this ref true (and navigates to an empty draft), so on
   * the resumed turn the tool runs in BUILD mode — it emits a CREATE page-gen
   * on the CURRENT draft (no nav) and collects widget digests for a grounded
   * summary, instead of offering again. The runner resets it on consumption.
   */
  pendingCustomPageBuildRef: React.MutableRefObject<boolean>;
  /**
   * Emit a CREATE `startCustomPageStream` command for the CURRENT draft (no
   * navigation, no new draft) — the build-here path. The drawer already created
   * + navigated to the empty draft at chip-click time; this just emits the
   * build for it. Resolved via the page-collection channel by the runner.
   */
  emitCreateCurrentPage: (requestId: string, prompt: string) => void;
  /**
   * Append a `report_offers` message with one chip per `reportType` (the user
   * clicks a chip to launch), mirroring the drawer's report-offer shape.
   * Returns the resolved display names (skipping unresolvable types) for the
   * model's narration.
   */
  offerReport: (
    reportTypes: string[],
    prompt: string
  ) => { reportNames: string[]; messageId?: string };
  /** Current page display name / pathname for the filter digest target. */
  currentPageLabel: () => string;
  /** Default entity type for the current route, used when resolving filter target. */
  inferEntityTypeForPath: (path: string) => GlobalAssistantEntityType;
  /**
   * Build the per-turn step context (taxonomy, current-page, custom-dash
   * edit-mode) from the same sources the legacy `suggest`/`runAssistantPipeline`
   * path used. Called once at the start of each turn. `conversationSummary` is
   * supplied separately by the runner. Fields are omitted when unavailable.
   */
  buildTurnContext: () => TurnStepContextFields;
};

/**
 * The non-summary fields of the turn-step context, assembled by the drawer
 * from taxonomy/current-page/custom-dash state and threaded into the loop.
 */
export type TurnStepContextFields = Omit<
  NonNullable<TurnStepRequestBody['context']>,
  'conversationSummary'
>;

export type UseGlobalAiTurnRunnerResult = {
  runTurn: (
    prompt: string,
    options?: { suppressReportOffer?: boolean }
  ) => Promise<void>;
};

export function useGlobalAiTurnRunner(
  deps: GlobalAiTurnRunnerDeps
): UseGlobalAiTurnRunnerResult {
  const {
    transcript,
    conversationSummary,
    appendTranscript,
    updateAssistantMessageText,
    updateReasoningTrace,
    setGenerationStatus,
    chartPreviewRuntimeByMessageId,
    mergeChartPreviewRuntime,
    addChartPreviewTimelineEvent,
    turnAbortRef,
  } = useGlobalAIAssistant();

  // Read the live runtime map through a ref so the chart-settle poller sees
  // the latest snapshot without re-subscribing per turn.
  const runtimeRef = useRef(chartPreviewRuntimeByMessageId);
  runtimeRef.current = chartPreviewRuntimeByMessageId;

  // Latest transcript for async callbacks that resolve after their closure was
  // created (segment append-dedupe reads this through the ref-tracked Set).
  const transcriptRef = useRef(transcript);
  transcriptRef.current = transcript;

  const depsRef = useRef(deps);
  depsRef.current = deps;

  const runTurn = useCallback(
    async (
      userPrompt: string,
      options?: { suppressReportOffer?: boolean }
    ): Promise<void> => {
      const currentDeps = depsRef.current;

      // Per-turn reasoning trace state.
      const traceId = crypto.randomUUID();
      let trace: ReasoningTrace = emptyTrace(Date.now());
      let traceAppended = false;
      let pendingDescription = '';
      let activeToolStepId: string | null = null;

      // Abort any in-flight turn and open a fresh controller for this one, so
      // closing the drawer (which aborts `turnAbortRef`) stops this turn's SSE
      // and poll waits instead of letting them run to their multi-second
      // timeouts and write to the store/transcript after the drawer is gone
      // (FE-2117).
      turnAbortRef.current?.abort();
      const turnController = new AbortController();
      turnAbortRef.current = turnController;
      const { signal } = turnController;
      // Dev-only eval recording (no-op in production builds).
      evalRecordTurnStart(userPrompt);
      let turnErrored = false;

      // --- conversation history -> priorMessages ---
      const bounded = buildBoundedConversationContext({
        transcript: transcriptRef.current,
        summary: conversationSummary,
      });
      const priorMessages: TurnMessage[] = bounded.turns.map((turn) =>
        turn.role === 'user'
          ? { role: 'user', content: turn.content }
          : { role: 'assistant', content: turn.content }
      );

      // --- render_chart: append a single_chart message, await its runtime
      //     snapshot settling, resolve the data digest the data reporter
      //     publishes (or a synthetic empty digest on timeout). ---
      // Id of the turn's most recent single-VALUE answer chart that has a
      // matching standard page — the only kind of chart `offer_standard_page`
      // can target. Gated to those renders (not every render_chart) so a later
      // ordinary chart in the same turn can't hijack the page offer's target.
      let lastStandardPageAnswerChartId: string | undefined;
      // Id of the turn's most recent RENDERED (non-single-value) chart. The
      // model's post-chart narration may carry offer:page / offer:filters
      // markers; onAnswer stamps them onto this chart so the drawer renders
      // inline triggers instead of the button chips.
      let lastRenderedChartId: string | undefined;
      const renderChartAndAwaitDigest = async (payload: {
        config: unknown;
        filters?: unknown;
        entityType?: string;
        intentSummary?: string;
        chartSubtitle?: string;
        standardPagePath?: string;
      }): Promise<ChartDataDigest> => {
        const chartMessageId = crypto.randomUUID();
        const payloadEntity = payload.entityType as
          | GlobalAssistantEntityType
          | undefined;
        const entityType: CustomDashAssistantEntityType =
          isCustomDashAssistantEntityType(payloadEntity)
            ? payloadEntity
            : 'company';
        const primaryFilterId = PRIMARY_FILTER_BY_AI_ENTITY[entityType];
        const { items } = getValidatedPositionedItemsFromLlmContent(
          payload.config as LLMTabContentForDispatch,
          primaryFilterId
        );
        const firstItem = items[0];
        if (!firstItem) {
          // No renderable chart — return an empty digest so the model can
          // narrate the absence rather than hang on a chart that never mounts.
          return {
            kind: 'chart_data',
            metric: payload.intentSummary ?? 'chart',
            chartType: 'unknown',
            rowCount: 0,
            empty: true,
          };
        }

        appendTranscript({
          id: chartMessageId,
          role: 'assistant',
          kind: 'single_chart',
          item: firstItem,
          filters: payload.filters as
            | TalentDiscoveryAiFilterSearchResponseV2
            | undefined,
          entityType,
          ...(payload.intentSummary
            ? { intentSummary: payload.intentSummary }
            : {}),
          ...(payload.chartSubtitle
            ? { chartSubtitle: payload.chartSubtitle }
            : {}),
          ...(payload.standardPagePath
            ? { standardPagePath: payload.standardPagePath }
            : {}),
          prompt: userPrompt,
        });
        addChartPreviewTimelineEvent(chartMessageId, 'chart message appended');
        lastRenderedChartId = chartMessageId;
        // Deliberately omit `narrationPrompt`: the model narrates the chart as
        // its next text segment, so the auto-narration controller must NOT
        // fire. The chart still renders + reports its data digest.
        mergeChartPreviewRuntime(chartMessageId, { phase: 'mounting' });

        // The chart is already visible; only the narration waits on its data
        // digest. Surface a specific status so the chat isn't an opaque spinner
        // during the settle. Superseded by the model's narration on the next
        // step (or cleared at turn end).
        setGenerationStatus('Reading the chart data…');
        const settled = await awaitChartSettle(
          runtimeRef,
          chartMessageId,
          signal
        );
        addChartPreviewTimelineEvent(chartMessageId, 'settle detected');
        if (process.env.NODE_ENV === 'development') {
          // The add above lands via async setState — append the settle mark
          // locally so this log's summary includes it.
          const timeline = [
            ...(runtimeRef.current[chartMessageId]?.timeline ?? []),
            { atMs: Date.now(), event: 'settle detected' },
          ];
          console.info(
            '[ga-latency]',
            payload.intentSummary ?? 'chart',
            summarizeChartPreviewTimeline(timeline),
            timeline
          );
        }
        if (settled?.dataDigest) {
          // Carry the chart's SCOPE (intentSummary, else subtitle) into the
          // digest title so the grounded narration knows WHAT the chart shows
          // (e.g. "headcount of Asian men at Google"), not just the bare metric,
          // AND the explicit applied-filter labels (`filtersApplied`) so the
          // model has structured ground truth those filters resolved + applied.
          // Without these the model narrates the value as unfiltered and invents
          // "the filter did not resolve" against a single-row result (FE-2117).
          return withChartScope(settled.dataDigest, {
            intentSummary: payload.intentSummary,
            chartSubtitle: payload.chartSubtitle,
            filtersApplied: summarizeAppliedChartFilters(
              payload.filters as
                | TalentDiscoveryAiFilterSearchResponseV2
                | undefined
            ),
          });
        }
        const chartType =
          (firstItem.plot && 'chartType' in firstItem.plot
            ? firstItem.plot.chartType
            : undefined) ?? 'unknown';
        // The chart settled in an ERROR phase (the data layer failed) — emit an
        // error digest so the model narrates a FAILURE rather than "no data".
        // A timeout / non-error settle with no digest stays a synthetic empty.
        if (settled?.phase === 'error') {
          return {
            kind: 'chart_data',
            metric: payload.intentSummary ?? payload.chartSubtitle ?? 'chart',
            chartType: String(chartType),
            rowCount: 0,
            empty: true,
            error: true,
          };
        }
        // Synthetic empty digest derived from the payload + item.
        return {
          kind: 'chart_data',
          metric: payload.intentSummary ?? payload.chartSubtitle ?? 'chart',
          chartType: String(chartType),
          rowCount: 0,
          empty: true,
        };
      };

      // --- Headless digest fetch (DP3): the single-value answer path. The
      //     digest comes from the offscreen digest host — no transcript
      //     message, no drawer slot, no chart mount, no settle polling. ---
      const fetchChartDigestHeadless = async (payload: {
        config: unknown;
        filters?: unknown;
        entityType?: string;
        intentSummary?: string;
        chartSubtitle?: string;
      }): Promise<ChartDataDigest> => {
        const payloadEntity = payload.entityType as
          | GlobalAssistantEntityType
          | undefined;
        const entityType: CustomDashAssistantEntityType =
          isCustomDashAssistantEntityType(payloadEntity)
            ? payloadEntity
            : 'company';
        const primaryFilterId = PRIMARY_FILTER_BY_AI_ENTITY[entityType];
        const { items } = getValidatedPositionedItemsFromLlmContent(
          payload.config as LLMTabContentForDispatch,
          primaryFilterId
        );
        const firstItem = items[0];
        if (!firstItem) {
          // Same contract as renderChartAndAwaitDigest: empty digest, never hang.
          return {
            kind: 'chart_data',
            metric: payload.intentSummary ?? 'chart',
            chartType: 'unknown',
            rowCount: 0,
            empty: true,
          };
        }
        setGenerationStatus('Looking up the figure…');
        const digest = await fetchHeadlessChartDigest(
          {
            item: firstItem,
            filters: payload.filters as
              | TalentDiscoveryAiFilterSearchResponseV2
              | undefined,
            entityType,
          },
          { signal }
        );
        // Same scope enrichment the legacy path applies (intent + applied-
        // filter labels ground the narration — FE-2117).
        return withChartScope(digest, {
          intentSummary: payload.intentSummary,
          chartSubtitle: payload.chartSubtitle,
          filtersApplied: summarizeAppliedChartFilters(
            payload.filters as
              | TalentDiscoveryAiFilterSearchResponseV2
              | undefined
          ),
        });
      };

      // Append the card-less single_chart transcript message that carries the
      // offer:page wiring (standardPagePath + filters + prompt — the drawer's
      // inline-link click reads them). answerAsText messages are NEVER mounted
      // (excluded from chart-preview liveness), so this is pure metadata — the
      // digest itself came from the headless service. Success path only.
      const appendAnswerContextChart = (payload: {
        config: unknown;
        filters?: unknown;
        entityType?: string;
        intentSummary?: string;
        standardPagePath: string;
      }): void => {
        const payloadEntity = payload.entityType as
          | GlobalAssistantEntityType
          | undefined;
        const entityType: CustomDashAssistantEntityType =
          isCustomDashAssistantEntityType(payloadEntity)
            ? payloadEntity
            : 'company';
        const primaryFilterId = PRIMARY_FILTER_BY_AI_ENTITY[entityType];
        const { items } = getValidatedPositionedItemsFromLlmContent(
          payload.config as LLMTabContentForDispatch,
          primaryFilterId
        );
        const firstItem = items[0];
        if (!firstItem) return;
        const chartMessageId = crypto.randomUUID();
        appendTranscript({
          id: chartMessageId,
          role: 'assistant',
          kind: 'single_chart',
          item: firstItem,
          filters: payload.filters as
            | TalentDiscoveryAiFilterSearchResponseV2
            | undefined,
          entityType,
          ...(payload.intentSummary
            ? { intentSummary: payload.intentSummary }
            : {}),
          standardPagePath: payload.standardPagePath,
          answerAsText: true,
          prompt: userPrompt,
        });
        lastStandardPageAnswerChartId = chartMessageId;
      };

      // --- create_custom_page (BUILD-HERE mode): emit a CREATE page-gen for
      //     the CURRENT draft (the chip click already navigated us to the empty
      //     draft), stream its page-gen progress to the activity lane, then
      //     await the page's collected widget digests so the model narrates a
      //     grounded CREATE summary of the actual widgets. Mirrors the
      //     `updateCurrentPage` collection logic but emits mode:'create'. ---
      const buildCustomPageHereAndCollect = async (
        prompt: string,
        emit?: ToolEmit
      ): Promise<{
        ok: boolean;
        digests: ChartDataDigest[];
        expectedCount?: number;
      }> => {
        const requestId = crypto.randomUUID();
        const store = useGlobalAiCommandStore.getState();
        let lastProgressSeq = store.lastPageGenProgress?.seq ?? 0;
        // Baseline the collection channel BEFORE emitting: the bridge's
        // NarrationCollector publishes `lastPageCollectionOutcome` once the
        // built page's widgets have all reported their data. We poll for an
        // outcome whose `at` advanced past this baseline so the model narrates
        // a grounded summary of the ACTUAL widgets, not a blind template.
        const baselineCollectionAt = store.lastPageCollectionOutcome?.at ?? 0;
        let settled = false;
        const unsubscribeProgress = useGlobalAiCommandStore.subscribe(
          (state) => {
            const p = state.lastPageGenProgress;
            if (!p || p.seq <= lastProgressSeq || settled || signal.aborted) {
              return;
            }
            lastProgressSeq = p.seq;
            emit?.(routePageGenProgress(p.event));
          }
        );
        try {
          // Immediate t=0 status before the first stream event lands.
          emit?.({ tier: 'status', text: 'Designing your dashboard…' });
          // Read the LIVE deps ref, not the turn-start snapshot: the drawer impl
          // closes over `window.location.pathname` to target the current draft.
          depsRef.current.emitCreateCurrentPage(requestId, prompt);
          const ok = await waitForCommandComplete(
            requestId,
            COMMAND_COMPLETE_TIMEOUT_MS,
            signal
          );
          if (!ok) {
            return { ok: false, digests: [] };
          }
          // Settle phase: stop emitting late build status, then await the
          // collector so we hand the model the page's real widget digests.
          settled = true;
          setGenerationStatus('Analyzing the page results…');
          const outcome = await awaitPageCollection(
            baselineCollectionAt,
            signal
          );
          return {
            ok: true,
            digests: outcome?.digests ?? [],
            expectedCount: outcome?.expectedCount,
          };
        } finally {
          settled = true;
          unsubscribeProgress();
        }
      };

      const handles: ClientToolHandles = {
        navigateToOption: (optionId) =>
          currentDeps.navigateToOptionId(optionId),
        // The `navigate` tool is terminal: it never drives the router. Resolve
        // the optionId to a destination name and append a confirmation chip
        // carrying the user's prompt so a click can resume any chained task.
        // The chip's click handler performs the guarded navigation.
        requestNavigateConfirm: (
          optionId,
          withContinuation = true,
          grounded?: {
            resolvedFilters: TalentDiscoveryAiFilterSearchResponseV2;
            entityType?: GlobalAssistantEntityType;
          }
        ) => {
          const match = getStandardPageMatchForOptionId(optionId, {});
          if (!match) {
            return { unknownPage: true } as const;
          }
          // ALWAYS append the nav_confirm chip — home and off-home alike.
          // Nothing auto-navigates: the chip / inline trigger is fired by an
          // explicit user click everywhere (home behaves exactly like off-home).
          // A click performs the guarded navigation and runs any chained task,
          // so a compound request (open page + filter) still completes via the
          // continuationPrompt. Preserving continuationPrompt / withContinuation
          // here keeps that behavior.
          const messageId = crypto.randomUUID();
          appendTranscript({
            id: messageId,
            role: 'assistant',
            kind: 'nav_confirm',
            optionId,
            pageName: match.name,
            // When continuation is suppressed (offer_page_or_custom's page chip),
            // omit the prompt so the chip just navigates on click — no resumed
            // turn that would try to answer this prompt by filtering the page.
            ...(withContinuation ? { continuationPrompt: userPrompt } : {}),
            // Data-grounded offer: carry the answer's exact resolved artifact
            // so the click applies it on the destination (text=link contract).
            ...(grounded
              ? {
                  filters: grounded.resolvedFilters,
                  ...(grounded.entityType
                    ? { entityType: grounded.entityType }
                    : {}),
                }
              : {}),
          });
          return { pageName: match.name, messageId } as const;
        },
        applyFilters: async (filterResponse, target, merge) => {
          const requestId = crypto.randomUUID();
          // Honor `target`: 'talent_discovery' applies to the Talent /
          // Individual Discovery surface; 'company_discovery' applies to the
          // Company Discovery (screener) surface. Either way we navigate there
          // first if the user is not already on it. 'current_page' (or any
          // unrecognised value) falls back to the current route — unchanged
          // from prior behavior. The resolved `path` drives both the
          // filterKind (screener vs tdV2) and the inferred entity type.
          const discoveryTarget = DISCOVERY_TARGETS[target];
          const currentPath = currentDeps.currentPageLabel();
          const path = discoveryTarget?.path ?? currentPath;

          // Navigate to the discovery surface first if we are not already there.
          if (discoveryTarget && currentPath !== discoveryTarget.path) {
            await currentDeps.navigateToOptionId(discoveryTarget.optionId);
          }

          // Read the LIVE deps ref, not the turn-start snapshot: when this turn
          // navigated mid-loop (navigate -> search_filters -> apply_filters) a
          // re-render swapped the page-derived closure, so the turn-start
          // `currentDeps.inferEntityTypeForPath` would infer for the page we LEFT.
          const entityType = depsRef.current.inferEntityTypeForPath(path);
          if (isFilterResponse(filterResponse)) {
            // The `search_filters` server tool already resolved the filters
            // into this artifact. Apply it directly through the bridge's
            // route-filter machinery WITHOUT re-running AI resolution.
            currentDeps.emitResolvedRouteFilters({
              requestId,
              path,
              response: filterResponse,
              prompt: userPrompt,
              entityType,
              merge,
            });
            // Optimistic (DP4): the digest is built from the artifact, not
            // from page completion — the bridge consumes the command when the
            // target page mounts, and both failure modes (TTL expiry, bridge
            // apply error) surface in-chat via lastFilterApplyFailure. Not
            // waiting saves the page-apply latency (up to COMMAND_TTL_MS on a
            // missed apply) from the turn.
            return { response: filterResponse, targetPage: path };
          }
          // Defensive fallback (shouldn't happen): no usable artifact, so
          // resolve from the USER PROMPT — never from `target`. The AWAIT here
          // is the RESOLUTION (it produces the digest content); only the page
          // completion wait is skipped, same as the artifact path above.
          const response = await currentDeps.resolveAndEmitRouteFilters({
            requestId,
            path,
            promptForFilters: userPrompt,
            entityType,
          });
          return { response, targetPage: path };
        },
        renderChartAndAwaitDigest,
        fetchChartDigest: fetchChartDigestHeadless,
        appendAnswerContextChart,
        requestCreateCustomPage: async (
          prompt,
          entityType,
          emit,
          forceOffer,
          groundedFilters
        ) => {
          const requestedEntity = entityType as
            | GlobalAssistantEntityType
            | undefined;
          const cdEntityType: CustomDashAssistantEntityType =
            isCustomDashAssistantEntityType(requestedEntity)
              ? requestedEntity
              : 'company';
          if (forceOffer) {
            // Pure OFFER (offer_page_or_custom): never build. Clear any stale
            // pending-build ref so a build armed-but-not-consumed earlier (e.g.
            // the user started a new prompt mid-build) can't poison this into a
            // silent BUILD-HERE. Fall through to the OFFER path below.
            currentDeps.pendingCustomPageBuildRef.current = false;
          } else if (currentDeps.pendingCustomPageBuildRef.current) {
            // BUILD-HERE: the user clicked "Build the dashboard", which created
            // an empty draft, navigated us to it, armed this ref, and resumed
            // the turn. Run the CREATE page-gen on THIS draft (no nav, no new
            // draft) and return widget digests for a grounded CREATE summary.
            // Reset the ref immediately so a later create_custom_page in this
            // (or a future) turn offers again rather than silently building.
            currentDeps.pendingCustomPageBuildRef.current = false;
            const { ok, digests, expectedCount } =
              await buildCustomPageHereAndCollect(prompt, emit);
            // expectedCount must ride along or the create_custom_page tool's
            // partial-aware narration branch never fires and a 3-of-6-settled
            // cold build gets the absolute "ONLY charts" wording (Greptile).
            return { built: true as const, ok, digests, expectedCount };
          }
          // RESOLVE-FIRST OFFER (Phase C, meeting 2026-07-08): for the explicit
          // create_custom_page offer, run the page-gen stream HEADLESSLY now —
          // the streamed thinking rides the existing progress sink into the
          // activity lane — and attach the finished layout to the Build chip.
          // The click then seeds a draft with the layout and navigates: no
          // empty draft, no on-page build, no resumed turn. Generation failure
          // or a clarification-questions event falls back to the LEGACY
          // confirm-navigate-resume chip (progressive enhancement, never a
          // dead end). offer_page_or_custom (forceOffer) stays legacy: its
          // build chip is a secondary option — pre-generating a full page for
          // every partial-fit offer would tax every causal ask.
          let generated:
            | {
                content: LLMTabContentForDispatch;
                filters?: TalentDiscoveryAiFilterSearchResponseV2;
              }
            | undefined;
          if (!forceOffer) {
            generated =
              (await generateDashboardHeadless({
                prompt,
                entityType: cdEntityType,
                conversation: toConversationPayload(bounded),
                onProgress: (event) => {
                  const progress = dashboardStreamEventToProgress(event);
                  if (progress) {
                    emit?.(routePageGenProgress(progress));
                  }
                },
                signal,
              })) ?? undefined;
          }
          // Terminal OFFER tool: append the build-confirm chip and end the turn.
          // Store the FULL original user prompt (the message that triggered this
          // turn), NOT the tool's cleaned `prompt` argument — for the LEGACY
          // path the chip click navigates to an empty draft and RESUMES the
          // turn seeded with this prompt, and the resume must carry the user's
          // full "build a dashboard" intent so the model rebuilds the page
          // (calls create_custom_page in BUILD-HERE mode) rather than
          // re-routing a stripped prompt to a single chart.
          const messageId = crypto.randomUUID();
          appendTranscript({
            id: messageId,
            role: 'assistant',
            kind: 'create_page_confirm',
            prompt: userPrompt,
            entityType: cdEntityType,
            ...(generated ? { generated } : {}),
            // Data-grounded offer artifact — the lazy build's landing-filter
            // fallback (see the create_page_confirm.filters docblock).
            ...(groundedFilters ? { filters: groundedFilters } : {}),
          });
          return { offered: true as const, messageId };
        },
        updateCurrentPage: async (prompt, emit) => {
          const requestId = crypto.randomUUID();
          // Stream the update's reasoning to the activity lane, mirroring
          // createCustomPageAndCollect: subscribe to page-gen progress for the
          // duration of the update and route each NEW event through the sink, so
          // an update shows the same live "thinking" as a fresh build instead of
          // a static "Updating page…" (which reads as slower — FE review).
          const store = useGlobalAiCommandStore.getState();
          let lastProgressSeq = store.lastPageGenProgress?.seq ?? 0;
          // Baseline the collection channel BEFORE emitting: the bridge's
          // NarrationCollector publishes `lastPageCollectionOutcome` once the
          // updated/built page's widgets have all reported their data. We poll
          // for an outcome whose `at` advanced past this baseline so the model
          // can narrate a grounded summary of the ACTUAL widgets on the page —
          // critical for the confirm-navigate-resume build, where this tool
          // builds out the empty draft (otherwise it narrates the page blind).
          const baselineCollectionAt = store.lastPageCollectionOutcome?.at ?? 0;
          let settled = false;
          const unsubscribeProgress = useGlobalAiCommandStore.subscribe(
            (state) => {
              const p = state.lastPageGenProgress;
              if (!p || p.seq <= lastProgressSeq || settled || signal.aborted) {
                return;
              }
              lastProgressSeq = p.seq;
              emit?.(routePageGenProgress(p.event));
            }
          );
          try {
            // Read the LIVE deps ref, not the turn-start snapshot: the drawer
            // impl closes over React Router's `location.pathname` to pick the
            // entity type + target page. A mid-turn navigation re-renders and
            // swaps that closure, so the turn-start snapshot would target the
            // page we LEFT.
            depsRef.current.emitUpdateCurrentPage(requestId, prompt);
            // Whether the command actually confirmed (true) or timed out (false)
            // so the client tool narrates an honest result (FE-2117).
            const ok = await waitForCommandComplete(
              requestId,
              COMMAND_COMPLETE_TIMEOUT_MS,
              signal
            );
            if (!ok) {
              return { ok: false as const, digests: [] };
            }
            // Settle phase: stop emitting late build status, then await the
            // collector so we hand the model the page's real widget digests.
            settled = true;
            const outcome = await awaitPageCollection(
              baselineCollectionAt,
              signal
            );
            return {
              ok: true as const,
              digests: outcome?.digests ?? [],
              expectedCount: outcome?.expectedCount,
            };
          } finally {
            settled = true;
            unsubscribeProgress();
          }
        },
        offerReport: async (reportTypes, prompt) =>
          currentDeps.offerReport(reportTypes, prompt),
        // Append the offer tool's `narration` param as a normal assistant text
        // message. The drawer renders `text` messages through
        // `parseOfferSegments`, so the `[…](offer:nav|build)` markers become
        // inline trigger spans and `turnNarrationHasOffer` suppresses the
        // redundant chip. Reuses the same `appendTranscript` the final answer
        // uses (onAnswer), so ordering/rendering match a model narration.
        appendAssistantText: (text, offerTargets) => {
          appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'text',
            text,
            ...(offerTargets ? { offerTargets } : {}),
          });
        },
        getLastStandardPageAnswerChartId: () => lastStandardPageAnswerChartId,
        askUser: (question, options) => {
          appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'clarification',
            text: question,
            ...(options
              ? { clarificationOptions: options as GlobalAssistantEntityType[] }
              : {}),
          });
        },
      };

      const toolDeps = createClientToolDeps(handles);

      // Assemble the full turn-step context from the drawer's live
      // taxonomy/current-page/custom-dash state plus the bounded summary.
      // Only include fields that are actually present.
      // Read the LIVE deps ref, not the turn-start snapshot: `buildTurnContext`
      // reads `location.pathname`, which a mid-turn navigation can swap via a
      // re-render. Called once at turn start today (so not yet exploitable), but
      // read live for consistency/defense with the other page-derived deps.
      const contextFields = depsRef.current.buildTurnContext();
      const context: NonNullable<TurnStepRequestBody['context']> = {
        ...contextFields,
        ...(bounded.summary ? { conversationSummary: bounded.summary } : {}),
      };
      const hasContext = Object.keys(context).length > 0;

      // Per-turn progress sink: lets a running tool stream `status` ticks to
      // the activity lane and `narrate` checkpoints to the chat. No-op until a
      // tool emits (a later task wires create_custom_page).
      const progress = createToolProgressSink({
        userGoal: userPrompt,
        ...(hasContext ? { context } : {}),
        signal,
        newId: () => crypto.randomUUID(),
        appendTranscript,
        updateAssistantMessageText,
        setGenerationStatus,
        onThinking: (text) => {
          if (!activeToolStepId) return;
          trace = setStepThinking(trace, activeToolStepId, text);
          updateReasoningTrace(traceId, trace);
        },
      });

      // Kick off the deterministic report selector in parallel with the turn
      // loop. We never await this here, so it adds no latency to the turn; we
      // resolve it only after the loop produces an analytical outcome below.
      const reportSelectionPromise = postGlobalAssistantSelectReport({
        prompt: userPrompt,
      }).catch(() => null);

      // Per-turn flags driving the post-loop report ride-along. We offer a
      // matching report only when the turn produced an analytical outcome
      // (custom page or chart) AND the model did not already offer one itself.
      let analyticalOutcomeRan = false;
      let modelOfferedReport = false;

      setGenerationStatus('Thinking…');
      try {
        await runTurnLoop({
          userPrompt,
          priorMessages,
          signal,
          ...(hasContext ? { context } : {}),
          postStep: runTurnStep,
          executeTool: (name, input, artifacts) => {
            // A single-value `answerAsText` render is a plain text reply, not a
            // chart/page deliverable — it must NOT trigger the post-loop report
            // ride-along. The flag rides on the chart_config artifact.
            const isTextAnswer =
              name === 'render_chart' &&
              input !== null &&
              typeof input === 'object' &&
              'configArtifactId' in input
                ? (
                    artifacts.get(
                      String(
                        (input as { configArtifactId: unknown })
                          .configArtifactId
                      )
                    ) as { answerAsText?: boolean } | undefined
                  )?.answerAsText === true
                : false;
            if (
              name === 'create_custom_page' ||
              name === 'offer_page_or_custom' ||
              (name === 'render_chart' && !isTextAnswer)
            ) {
              analyticalOutcomeRan = true;
            }
            if (name === 'offer_report') {
              modelOfferedReport = true;
            }
            const result = executeClientTool(
              name,
              input,
              artifacts,
              toolDeps,
              progress.emit
            );
            // Close the progress sink once an analytical tool resolves: it is the
            // turn's last narration-producing step, so any narrate still in
            // flight must be dropped rather than land after the model's final
            // response (chat ordering).
            if (
              name === 'create_custom_page' ||
              name === 'offer_page_or_custom' ||
              name === 'render_chart'
            ) {
              return result.finally(() => progress.close());
            }
            return result;
          },
          onText: () => {
            // Intentionally a no-op: loop narration is rendered as grey step
            // descriptions in the reasoning trace (via onStepDescribe/onStepTool).
            // The single black answer message is produced by onAnswer. Rendering
            // narration here too caused the grey step + black text duplication.
          },
          onStatus: (content) => setGenerationStatus(content),
          onError: (message) => {
            appendTranscript({
              id: crypto.randomUUID(),
              role: 'assistant',
              kind: 'error',
              text: message,
            });
          },
          onStepStart: (_roundId) => {
            // Lazy-create the trace element on the turn's first step event.
            // Steps are created per-TOOL (in onStepTool), not per-round, so
            // they render in execution order even when a round runs several
            // server tools before its client tool.
            if (!traceAppended) {
              trace = emptyTrace(Date.now());
              appendTranscript({
                id: traceId,
                role: 'assistant',
                kind: 'reasoning_trace',
                trace,
              });
              traceAppended = true;
            }
          },
          onStepDescribe: (_roundId, text) => {
            // Buffer the round's narration; it becomes the description of the
            // round's FIRST tool step (cleared once consumed).
            pendingDescription = text;
          },
          onStepTool: (stepId, name, input, artifacts) => {
            trace = startStep(trace, stepId);
            // A single-value answer renders no chart, so relabel render_chart's
            // step from "Rendering the chart" — the answerAsText flag rides on the
            // chart_config artifact this render_chart call points at.
            const answerAsText =
              name === 'render_chart' &&
              input !== null &&
              typeof input === 'object' &&
              'configArtifactId' in input
                ? (
                    artifacts?.get(
                      String(
                        (input as { configArtifactId: unknown })
                          .configArtifactId
                      )
                    ) as { answerAsText?: boolean } | undefined
                  )?.answerAsText === true
                : false;
            trace = titleStep(
              trace,
              stepId,
              toolStepTitle(name, input, { answerAsText })
            );
            // `ask_user` ends the turn WITHOUT firing onAnswer (which is what
            // normally clears the buffered narration), so a pre-question "I'll
            // generate…" clause would otherwise stick to the ask_user step as a
            // stale grey description while the actual outcome is a clarifying
            // question — the question itself is already rendered as its own
            // clarification message (FE-2171). Skip the description for ask_user;
            // always clear the buffer so it can't bleed into the next round.
            if (pendingDescription && name !== 'ask_user') {
              trace = describeStep(trace, stepId, pendingDescription);
            }
            pendingDescription = '';
            activeToolStepId = stepId;
            updateReasoningTrace(traceId, trace);
          },
          onStepEnd: (stepId, status) => {
            trace = finishStep(trace, stepId, status);
            if (activeToolStepId === stepId) activeToolStepId = null;
            updateReasoningTrace(traceId, trace);
          },
          onAnswer: (roundId, text) => {
            // The narration is the final answer, not a step. Only touch the trace
            // if one was actually appended this turn — a text-only turn (no tool)
            // never created a reasoning_trace, so skip the mutations to avoid a
            // spurious re-render. When a terminal tool (offer_report/ask_user)
            // ended the turn, its step carries the narration as a description;
            // clear it so the grounded answer isn't duplicated.
            if (traceAppended) {
              trace = endTrace(trace, Date.now());
              trace = describeStep(trace, roundId, '');
              updateReasoningTrace(traceId, trace);
            }
            // The round's narration was the answer, not a step description —
            // clear pending so it doesn't bleed into the next turn.
            pendingDescription = '';
            // Render the final answer as a normal text message. onAnswer fires at
            // most once per turn, so a plain append needs no dedup guard. When the
            // turn rendered a chart and the narration carries post-chart offer
            // markers, stamp them onto that chart so the drawer wires the inline
            // triggers (and suppresses the matching chips). Marker omitted → no
            // stamp → the chips fall back (last resort, by design).
            const chartOfferTargets: Partial<Record<OfferKind, string>> = {};
            if (lastRenderedChartId) {
              for (const kind of ['page', 'filters'] as const) {
                if (hasOfferMarker(text, kind)) {
                  chartOfferTargets[kind] = lastRenderedChartId;
                }
              }
            }
            appendTranscript({
              id: roundId,
              role: 'assistant',
              kind: 'text',
              text,
              ...(Object.keys(chartOfferTargets).length
                ? { offerTargets: chartOfferTargets }
                : {}),
            });
          },
          onToolCall: () => {
            // Bookkeeping hook — no-op for now.
          },
        });

        // Report ride-along (product invariant, FE-2117): when the turn
        // produced an analytical outcome and the model did not already offer a
        // report, a matching prebuilt report is ALWAYS offered. Instead of
        // appending bare cards deterministically, resume the loop with a
        // seeded instruction so the MODEL makes the validated `offer_report`
        // call — the offer then renders as explained narration with inline
        // [Name](offer:report) links. On any failure (validation, refusal,
        // network), fall back to the deterministic card append so the
        // invariant holds. `select-report` is already confidence-gated on the
        // BE, so empty offers simply render nothing. Placed inside the `try`
        // after the loop so a thrown turn skips it.
        if (
          analyticalOutcomeRan &&
          !modelOfferedReport &&
          !options?.suppressReportOffer
        ) {
          const selection = await reportSelectionPromise;
          const offers = selection?.offers ?? [];
          const reportTypes = offers.map((o) => o.reportType);
          // Skip the ride-along if the turn was aborted (drawer closed) — don't
          // append a report offer to a turn the user walked away from.
          if (reportTypes.length > 0 && !signal.aborted) {
            // Silent loop resume: let the MODEL offer the report so the offer
            // arrives as explained narration with an inline [Name](offer:report)
            // link (validated by the offer_report schema), instead of a bare
            // card. One extra round; the main answer has already rendered.
            const reportLines = offers
              .map(
                (o) =>
                  `${o.reportName ?? o.reportType}${o.description ? ` — ${o.description}` : ''} (reportType: ${o.reportType})`
              )
              .join('; ');
            const seed = `[System: The user's request matches prebuilt report(s): ${reportLines}. Offer them NOW by calling offer_report ONCE with reportTypes [${reportTypes.join(', ')}], prompt set to the user's request, and a narration of 1–2 grounded sentences per report explaining why it fits this request and what the report contains, each clause ending on its [<Report Name>](offer:report) marker (one marker per report, in reportTypes order). The GLOBAL CAUSAL-CLAIM BAN applies to this narration: describe what each report CONTAINS, never assert that the user's outcome is caused by / driven by / due to / "traces to" anything. Do NOT render charts, navigate, build pages, ask questions, or emit free text — offer_report is the only action available.] ${userPrompt}`;
            let resumedOfferSucceeded = false;
            setGenerationStatus('Finding a matching report…');
            try {
              await runTurnLoop({
                userPrompt: seed,
                priorMessages,
                signal,
                ...(hasContext ? { context } : {}),
                postStep: runTurnStep,
                // offer_report is a single terminal action here. A SUCCESSFUL
                // call ends the loop via endsTurn; but the zero-chips path
                // returns a NON-terminal "No matching report" result, and since
                // this resume hard-restricts every other tool to an ERROR, the
                // model can only re-call offer_report — looping to the default
                // max steps (observed 5× on a causal answer, blowing latency).
                // Cap at 2: one real attempt plus one slack round, after which
                // the deterministic card fallback below fires.
                maxSteps: 2,
                // HARD-restrict the resume to offer_report: a non-compliant
                // round must not be able to render charts, navigate, or build
                // from inside this bolt-on step.
                executeTool: async (name, input, artifacts) => {
                  if (name !== 'offer_report') {
                    return {
                      resultForModel: `ERROR: only offer_report is available in this step — call offer_report as instructed.`,
                    };
                  }
                  const result = await executeClientTool(
                    name,
                    input,
                    artifacts,
                    toolDeps
                  );
                  if (result.endsTurn) {
                    resumedOfferSucceeded = true;
                  }
                  return result;
                },
                onText: () => {
                  // Stray streamed text is dropped — the offer_report narration
                  // param is this resume's only user-facing render.
                },
                onAnswer: () => {
                  // Same: never render the resume's free text.
                },
                onStatus: () => {
                  // Keep the single 'Finding a matching report…' status.
                },
                onError: () => {
                  // Swallow; the deterministic card fallback below covers it.
                },
                onToolCall: () => {
                  // Bookkeeping hook — no-op (matches the main loop).
                },
              });
            } catch {
              // Fall through to the deterministic fallback.
            }
            // Product invariant: a matching report is ALWAYS offered. If the
            // resume didn't produce the offer (validation failed twice, model
            // refused, network error), append the plain cards exactly as the
            // pre-resume ride-along did.
            if (!resumedOfferSucceeded && !signal.aborted) {
              depsRef.current.offerReport(reportTypes, userPrompt);
            }
          }
        }
      } catch (err) {
        if (signal.aborted) {
          // The user aborted (closed the drawer). Don't append an error bubble
          // — it would surface as a spurious failure when the drawer reopens.
          // Record the cancellation for telemetry only.
          void reportLoopError({
            source: 'sse_envelope',
            errorClass: 'user_cancelled',
          });
        } else {
          turnErrored = true;
          appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'error',
            text: 'Sorry — I ran into a problem completing that. Please try again.',
          });
          void reportLoopError({
            source: 'sse_envelope',
            errorClass:
              err instanceof Error && /cancel/i.test(err.message)
                ? 'user_cancelled'
                : 'unexpected',
          });
        }
      } finally {
        if (signal.aborted) {
          evalRecordTurnEnd('aborted');
        } else {
          evalRecordTurnEnd(turnErrored ? 'error' : 'done');
        }
        // Ensure the reasoning trace always closes — runTurnLoop returns
        // without onAnswer when a tool ends the turn (offer_report/ask_user),
        // on error/max-steps, on abort, or on a throw. Without this the UI
        // would render "Working…" forever.
        if (traceAppended && trace.endedAt === undefined) {
          trace = endTrace(trace, Date.now());
          updateReasoningTrace(traceId, trace);
        }
        // Only clear the shared ref if it still points at THIS turn — a newer
        // turn may have already replaced it.
        if (turnAbortRef.current === turnController) {
          turnAbortRef.current = null;
        }
        setGenerationStatus(null);
      }
    },
    [
      appendTranscript,
      conversationSummary,
      mergeChartPreviewRuntime,
      addChartPreviewTimelineEvent,
      setGenerationStatus,
      updateAssistantMessageText,
      updateReasoningTrace,
      turnAbortRef,
    ]
  );

  return { runTurn };
}

function isFilterResponse(
  value: unknown
): value is TalentDiscoveryAiFilterSearchResponseV2 | CompanyDiscoveryAiOutput {
  // Accept either the TD v2 shape (`filters` + root `relevantPromptText`) or
  // the screener `CompanyDiscoveryAiOutput` shape, which has `filters` but no
  // root `relevantPromptText` — instead it carries `unknownFilters` and nested
  // screener markers (`primary_selector` / `segments` / `entity_info_selectors`)
  // on `value.filters`. Without this branch a resolved screener artifact would
  // wrongly fall through to the prompt re-resolve fallback.
  if (typeof value !== 'object' || value === null || !('filters' in value)) {
    return false;
  }
  if ('relevantPromptText' in value || 'unknownFilters' in value) {
    return true;
  }
  const filters = (value as { filters: unknown }).filters;
  if (typeof filters !== 'object' || filters === null) {
    return false;
  }
  return (
    'primary_selector' in filters ||
    'segments' in filters ||
    'entity_info_selectors' in filters
  );
}

/**
 * Poll the chart-preview runtime snapshot for `messageId` until it reaches a
 * terminal state (`success` with a digest, or `error`) or the timeout elapses.
 * Returns the terminal snapshot, or `undefined` on timeout.
 */
async function awaitChartSettle(
  runtimeRef: React.MutableRefObject<
    Readonly<Record<string, GlobalAiChartPreviewRuntimeSnapshot>>
  >,
  messageId: string,
  signal?: AbortSignal
): Promise<GlobalAiChartPreviewRuntimeSnapshot | undefined> {
  const deadline = Date.now() + CHART_SETTLE_TIMEOUT_MS;
  for (;;) {
    const snapshot = runtimeRef.current[messageId];
    if (snapshot) {
      if (snapshot.phase === 'success' && snapshot.dataDigest) {
        return snapshot;
      }
      if (snapshot.phase === 'error') {
        return snapshot;
      }
    }
    // Bail on abort (drawer closed) as well as the wall-clock deadline.
    if (signal?.aborted || Date.now() >= deadline) {
      return snapshot;
    }
    await delay(CHART_SETTLE_POLL_MS);
  }
}

/**
 * Poll the command store for a `lastPageCollectionOutcome` whose `at`
 * timestamp has advanced past `baselineAt` (i.e. a NEW collection published
 * after we emitted the update command). Returns the outcome, or `undefined`
 * on timeout.
 */
async function awaitPageCollection(
  baselineAt: number,
  signal?: AbortSignal
): Promise<
  | NonNullable<
      ReturnType<
        typeof useGlobalAiCommandStore.getState
      >['lastPageCollectionOutcome']
    >
  | undefined
> {
  const deadline = Date.now() + PAGE_COLLECTION_TIMEOUT_MS;
  for (;;) {
    const outcome =
      useGlobalAiCommandStore.getState().lastPageCollectionOutcome;
    if (outcome && outcome.at > baselineAt) {
      return outcome;
    }
    // Bail on abort (drawer closed) as well as the wall-clock deadline.
    if (signal?.aborted || Date.now() >= deadline) {
      return undefined;
    }
    await delay(PAGE_COLLECTION_POLL_MS);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
